import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Eye, EyeOff, Copy, CheckCheck, Loader2, Lock, ShieldCheck } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRetrieveCredencial, type Credencial } from '@/lib/hooks/cofre';

// ─── Constantes ───────────────────────────────────────────────────────────────

const AUTO_CLOSE_SECONDS = 30;

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  userPassword: z.string().min(6, 'Mínimo 6 caracteres').max(200, 'Máximo 200 caracteres'),
});

type FormValues = z.infer<typeof schema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workspaceId: string;
  credencial: Credencial | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CofreRevealModal({ workspaceId, credencial, open, onOpenChange }: Props) {
  const [revealedSenha, setRevealedSenha] = useState<string | null>(null);
  const [showSenha, setShowSenha] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_CLOSE_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const retrieveMut = useRetrieveCredencial(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { userPassword: '' },
  });

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      reset();
      setRevealedSenha(null);
      setShowSenha(false);
      setShowUserPassword(false);
      setCopied(false);
      setCountdown(AUTO_CLOSE_SECONDS);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [open, reset]);

  // Countdown após revelar
  useEffect(() => {
    if (revealedSenha !== null) {
      setCountdown(AUTO_CLOSE_SECONDS);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            onOpenChange(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [revealedSenha, onOpenChange]);

  async function onSubmit(values: FormValues) {
    if (!credencial) return;
    try {
      const result = await retrieveMut.mutateAsync({
        credencialId: credencial.id,
        userPassword: values.userPassword,
      });
      setRevealedSenha(result.senha);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao revelar credencial.';
      if (msg.toLowerCase().includes('incorreta') || msg.includes('403')) {
        toast.error('Senha do GestAKI incorreta. Tente novamente.');
      } else {
        toast.error(msg);
      }
    }
  }

  async function copyToClipboard() {
    if (!revealedSenha) return;
    try {
      await navigator.clipboard.writeText(revealedSenha);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copiado.');
    } catch {
      toast.error('Falha ao copiar.');
    }
  }

  if (!credencial) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px]',
            'data-[state=open]:animate-fade-in',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(92vw,460px)]',
            'rounded-3 border border-card-stroke bg-card shadow-clay p-6',
            'data-[state=open]:animate-fade-in',
          )}
          aria-describedby="cofre-reveal-description"
        >
          {/* Cabeçalho */}
          <div className="flex items-start gap-3 mb-5">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-mint-soft text-mint-deep">
              <Lock className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="font-display text-[18px] font-bold tracking-[-0.015em] text-fg leading-tight">
                Revelar senha
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                id="cofre-reveal-description"
                className="text-sm text-fg-muted mt-0.5"
              >
                {credencial.portal} · {credencial.usuario}
              </DialogPrimitive.Description>
            </div>
          </div>

          {revealedSenha !== null ? (
            /* Estado: senha revelada */
            <div className="space-y-4">
              <div className="rounded-2 border border-card-stroke bg-card-soft p-4">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showSenha ? 'text' : 'password'}
                      readOnly
                      value={revealedSenha}
                      aria-label="Senha do portal"
                      className="flex h-9 w-full rounded-2 border border-card-stroke bg-card px-3 py-1 pr-10 font-mono text-sm text-fg shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-mint-deep select-all"
                    />
                    <button
                      type="button"
                      aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-dim hover:text-fg transition-colors"
                      onClick={() => setShowSenha((v) => !v)}
                    >
                      {showSenha ? (
                        <EyeOff className="h-4 w-4" strokeWidth={1.7} />
                      ) : (
                        <Eye className="h-4 w-4" strokeWidth={1.7} />
                      )}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={copyToClipboard}
                    className="shrink-0"
                  >
                    {copied ? (
                      <>
                        <CheckCheck className="h-4 w-4" strokeWidth={1.8} />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" strokeWidth={1.8} />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-fg-muted text-center">
                Esta janela fecha automaticamente em{' '}
                <span className="font-mono font-semibold text-warn">{countdown}s</span>
              </p>

              <div className="flex justify-end">
                <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          ) : (
            /* Estado: pedir senha do GestAKI */
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="h-4 w-4 text-mint-deep shrink-0" strokeWidth={1.8} />
                <p className="text-sm text-fg-muted">
                  Confirme sua identidade para decifrar e exibir a senha.
                </p>
              </div>

              <div>
                <Label htmlFor="reveal-userPassword" className="mb-1.5 block">
                  Sua senha do GestAKI
                </Label>
                <div className="relative">
                  <Input
                    id="reveal-userPassword"
                    type={showUserPassword ? 'text' : 'password'}
                    placeholder="Sua senha de acesso ao GestAKI"
                    autoComplete="current-password"
                    className="pr-10"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    {...register('userPassword')}
                  />
                  <button
                    type="button"
                    aria-label={showUserPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-dim hover:text-fg transition-colors"
                    onClick={() => setShowUserPassword((v) => !v)}
                  >
                    {showUserPassword ? (
                      <EyeOff className="h-4 w-4" strokeWidth={1.7} />
                    ) : (
                      <Eye className="h-4 w-4" strokeWidth={1.7} />
                    )}
                  </button>
                </div>
                {errors.userPassword && (
                  <p className="text-[11px] text-warn mt-1">{errors.userPassword.message}</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                      Decifando…
                    </>
                  ) : (
                    'Revelar senha'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
